import Vue from 'vue'
import VueMeta from 'vue-meta'
import PortalVue from 'portal-vue'

// We will use a replacement cookie for localStorage if the browser doesn't support localStorage
import '@/utils/local-storage'
import '@/utils/prop-types'
import '@/utils/type-checker'
import { setupTracking } from '@/utils/tracking'

import { getUserId } from '@/utils/auth'

// Styles
import '@/styles/index.scss'
import '@/styles/generic/_index.scss'

import { buildRootStore } from '@/store'
import { buildRootRouter } from '@/plugins/router'
import { buildRootLogger } from '@/utils/logger'

// Global Components
import ErrorBoundary from '@/error/error-boundary.vue'

import trackingService from '@tamara/customer-journey-tracker'

import globalMixin from '@/mixins/global.mixin'

import Config from './config'

import SvgIcon from './icons/svg-icon.vue'

import App from './App.vue'
import i18n from './plugins/i18n'
import gtmPlugin from './plugins/gtm'
import UserTrackingUtils from './plugins/user-tracking'
import vuetify from './plugins/vuetify'
import AbTestProvider, { initializeAbtProvider } from './plugins/abt'
import ClientLogger from './plugins/logger'

import * as filters from './filters' // global filters
import * as reactNativeWebviewCommunicator from './utils/react-native-webview-communicator'
// Default actions
import dispatchActionForAllModules from './utils/dispatch-action-for-all-modules'

import locator from './service-locator'

const REDIRECTING_FLAG = 'checkout_redirecting'

// IMPORTANT : statsig identifies each session with ID type you set in settings, so it is important to pass a unique ID right from begining.
// After discussion with Statsig team, we decided to leave the user object empty and let statsig pass stableID to identify the user. later when user logs in we send updateUser call to add userID and other details
let envTier = ''
if (Config.env === 'production') {
  envTier = 'production'
} else if (Config.env === 'staging' || Config.env === 'sandbox') {
  envTier = 'staging'
} else {
  envTier = 'development'
}

const urlParams = new URLSearchParams(window.location.search)
const orderId = urlParams.get('orderId')

// get user id from cookie if exists
const userId = getUserId()
const abtOptions = {
  sdkKey: Config.statsigKey,
  user: {
    userID: userId || null,
    customIDs: {
      order_id: orderId,
    },
  },
  sdkOptions: {
    environment: { tier: envTier },
  },
}

const userTrackingPluginOptions = {
  write_key: Config.RUDDERSTACK_WRITE_KEY,
  data_plane_url: Config.RUDDERSTACK_DATA_PLANE_URL,
  loadOptions: {
    useBeacon: false,
    beaconQueueOptions: {
      maxItems: 5,
      flushInterval: 500,
    },
  },
}

const updateViewHeight = () => {
  // First, we get the viewport height, and we multiply it by 1% to get a value for a vh unit
  const vh = window.innerHeight * 0.01
  // Then we set the value in the --vh custom property to the root of the document
  document.documentElement.style.setProperty('--vh', `${vh}px`)
}

const registerEventListeners = () => {
  window.addEventListener('resize', updateViewHeight)
  document.addEventListener('orientationchange', updateViewHeight)

  if ('virtualKeyboard' in navigator) {
    navigator.virtualKeyboard.addEventListener(
      'geometrychange',
      updateViewHeight
    )
  }
}

const configureVue = () => {
  // Register for 3rd party plugins
  Vue.use(VueMeta, { keyName: 'page' }) // The component option name that vue-meta looks for meta info on.
  Vue.use(PortalVue)

  // register global utility filters
  Object.keys(filters).forEach((key) => {
    Vue.filter(key, filters[key])
  })

  Vue.config.productionTip = false
  Vue.config.ignoredElements = ['apple-pay-button']

  // Catch error for Newrelic
  Vue.config.errorHandler = (err, vm, info) => {
    if (Config.isDev) {
      console.error(err)
    }

    if (window.newrelic) {
      const component = vm?.$options?.name || 'Anonymous'

      window.newrelic.setCustomAttribute('component', component)
      window.newrelic.setCustomAttribute('info', info)

      window.newrelic.noticeError(err)
    }
  }

  // Use custom plugin
  Vue.use(gtmPlugin)
  Vue.use(AbTestProvider)
  // new user tracking plugin
  Vue.use(UserTrackingUtils)
  Vue.use(trackingService, userTrackingPluginOptions)
  Vue.use(ClientLogger)

  // Add global component
  Vue.component('SvgIcon', SvgIcon)
  Vue.component('ErrorBoundary', ErrorBoundary)

  // Add global mixin
  Vue.mixin(globalMixin)

  Vue.prototype.$redirect = (redirectUrl) => {
    if (window[REDIRECTING_FLAG]) {
      locator.logger.warn(
        `Preventing redirection to URL because another redirection is already in progress.`,
        { data: { redirectUrl } }
      )

      return
    }

    window[REDIRECTING_FLAG] = true

    window.location.href = redirectUrl
  }
}

const setupVueInstance = (locator) => {
  // eslint-disable-next-line no-new
  return new Vue({
    el: '#app',
    router: locator.router,
    store: locator.store,
    i18n,
    vuetify,
    render: (h) => h(App),
  })
}

const setupApp = async () => {
  // Initialize Statsig in the background (non-blocking)
  // Statsig will work with cached/default values initially and update when ready
  initializeAbtProvider(
    abtOptions.sdkKey,
    abtOptions.user,
    abtOptions.sdkOptions
  )

  updateViewHeight()
  registerEventListeners()

  buildRootStore(locator)
  buildRootRouter(locator)
  buildRootLogger(locator)

  configureVue()

  const app = setupVueInstance(locator)
  // attach global window events and other initial setup
  setupTracking(app)
  // Automatically run the `init` action for every module if one exists.
  dispatchActionForAllModules('init')

  // for live debug
  window.__version__ = Config.env + '.' + Config.releaseVersion
  window.__locator__ = locator
  window.reactNativeWebviewCommunicator = reactNativeWebviewCommunicator
}

setupApp()
